import { Component, EventEmitter, Input, Output, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import type { CallAudit, CallAuditScores, SessionReport } from '../tracking-demo.models';

/**
 * The report's Audit tab — the backend call_audit verdict on one call: score,
 * what went well/wrong (with proof links into the RAG tab), grouped
 * recommendations, per-response grades, and the failed-query list.
 *
 * Everything shown is a post-hoc reconstruction (tool trail + judge model), and the
 * template says so. A null callScore (judge disabled -> status "partial") renders as
 * "not scored", never as 0 — a missing score and a bad score are different facts.
 */
@Component({
  selector: 'app-call-audit',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    @if (!audit()) {
    <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-6 text-sm text-gray-500">
      No audit for this call yet.
      @if (rerunFn) {
      <button class="ml-2 rounded-md border px-2 py-1 text-xs font-medium"
              [disabled]="running()" (click)="rerun()">
        {{ running() ? 'Auditing…' : 'Run audit now' }}
      </button>
      }
    </div>
    } @else {
    <div class="space-y-4">
      <!-- header: score + status -->
      <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-4 flex flex-wrap items-center gap-4">
        <div>
          <div class="text-3xl font-semibold">
            {{ audit()!.callScore ?? '—' }}<span class="text-sm text-gray-400">/100</span>
          </div>
          <div class="text-xs text-gray-500">
            @if (audit()!.callScore == null) { not scored (judge off or partial) }
            @else { call score (computed, not model-assigned) }
          </div>
        </div>
        <div class="text-xs">
          <span class="rounded-full px-2 py-0.5 font-medium"
                [class]="audit()!.status === 'complete' ? 'bg-emerald-100 text-emerald-700'
                         : 'bg-amber-100 text-amber-700'">{{ audit()!.status }}</span>
          <span class="ml-2 text-gray-500">{{ audit()!.counts?.graded ?? 0 }} of
            {{ audit()!.counts?.exchanges ?? 0 }} responses graded ·
            {{ audit()!.counts?.failures ?? 0 }} failure(s)</span>
        </div>
        <div class="ml-auto flex items-center gap-3">
          @for (dim of dims(); track dim.key) {
          <div class="text-center">
            <div class="text-sm font-semibold">{{ dim.value ?? '—' }}</div>
            <div class="text-[10px] uppercase tracking-wide text-gray-400">{{ dim.key }}</div>
          </div>
          }
          @if (rerunFn) {
          <button class="rounded-md border px-2 py-1 text-xs font-medium"
                  [disabled]="running()" (click)="rerun()">
            {{ running() ? 'Auditing…' : 'Re-run' }}
          </button>
          }
        </div>
      </div>
      <p class="text-[11px] text-gray-400">
        Reconstructed from the tool trail and a judge model — not the agent's actual reasoning.
      </p>

      @if (audit()!.narrative) {
      <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-4 text-sm">
        {{ audit()!.narrative }}
      </div>
      }

      <div class="grid gap-4 lg:grid-cols-2">
        <div class="rounded-xl border border-emerald-200 dark:border-emerald-900 p-4">
          <h4 class="mb-2 text-xs font-semibold uppercase text-emerald-600">What went well</h4>
          @for (w of audit()!.wentWell ?? []; track $index) {
          <div class="py-1 text-sm">{{ w.text }}</div>
          } @empty { <div class="text-sm text-gray-400">Nothing stood out.</div> }
        </div>
        <div class="rounded-xl border border-rose-200 dark:border-rose-900 p-4">
          <h4 class="mb-2 text-xs font-semibold uppercase text-rose-600">What went wrong</h4>
          @for (w of audit()!.wentWrong ?? []; track $index) {
          <div class="py-1 text-sm">{{ w.text }}</div>
          } @empty { <div class="text-sm text-gray-400">No failures detected.</div> }
        </div>
      </div>

      @if ((audit()!.recommendations ?? []).length) {
      <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-4">
        <h4 class="mb-2 text-xs font-semibold uppercase text-gray-500">Fix next</h4>
        @for (r of audit()!.recommendations; track $index) {
        <div class="flex items-start gap-2 py-1 text-sm">
          <span class="mt-0.5 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">{{ r.target }}</span>
          <div>
            <span>{{ r.text }}</span>
            @if (r.targetRef) { <span class="text-gray-500"> — {{ r.targetRef }}</span> }
            <span class="text-xs text-gray-400"> ({{ r.supportingExchanges.length }} response(s))</span>
          </div>
        </div>
        }
      </div>
      }

      @if ((audit()!.failures ?? []).length) {
      <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-4">
        <h4 class="mb-2 text-xs font-semibold uppercase text-gray-500">Failed queries</h4>
        @for (f of audit()!.failures; track $index) {
        <div class="border-t border-gray-100 py-2 text-sm first:border-t-0 dark:border-line">
          <div class="flex items-center gap-2">
            <span class="rounded px-1.5 py-0.5 text-[10px] font-medium"
                  [class]="f.severity === 'high' ? 'bg-rose-100 text-rose-700'
                           : f.severity === 'medium' ? 'bg-amber-100 text-amber-700'
                           : 'bg-gray-100 text-gray-600'">{{ f.severity }}</span>
            <span class="font-mono text-xs text-gray-500">{{ f.reason }}</span>
            @if (lookupIndexOf(f.evidence) !== null) {
            <button class="text-xs text-indigo-600 underline"
                    (click)="jumpToLookup(f.evidence)">view retrieval proof</button>
            }
          </div>
          <div class="mt-1 font-medium">“{{ f.query }}”</div>
          @if (f.answer) { <div class="text-gray-500">→ {{ f.answer }}</div> }
          @if (f.fix?.text) { <div class="text-xs text-emerald-700">fix: {{ f.fix?.text }}</div> }
        </div>
        }
      </div>
      }

      @if ((audit()!.responses ?? []).length) {
      <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-4">
        <h4 class="mb-2 text-xs font-semibold uppercase text-gray-500">Every graded response</h4>
        @for (r of audit()!.responses; track r.exchangeIndex) {
        <div class="border-t border-gray-100 py-2 text-sm first:border-t-0 dark:border-line">
          <button class="flex w-full items-center gap-2 text-left" (click)="toggle(r.exchangeIndex)">
            <span class="font-mono text-[10px] text-gray-400">#{{ r.exchangeIndex }}</span>
            <span class="flex-1 truncate">{{ r.question }}</span>
            <span class="font-mono text-xs"
                  [class]="r.reason === 'ok' ? 'text-emerald-600' : 'text-rose-600'">{{ r.reason }}</span>
            <span class="text-xs text-gray-400">{{ scoreLine(r.scores) }}</span>
          </button>
          @if (expanded().has(r.exchangeIndex)) {
          <div class="mt-2 space-y-1 pl-6 text-xs">
            <div class="text-gray-600">answer: {{ r.answer }}</div>
            @for (c of r.claims ?? []; track $index) {
            <div>
              <span class="rounded px-1 py-0.5 font-medium"
                    [class]="c.verdict === 'supported' ? 'bg-emerald-100 text-emerald-700'
                             : c.verdict === 'contradicted' ? 'bg-rose-100 text-rose-700'
                             : 'bg-amber-100 text-amber-700'">{{ c.verdict }}</span>
              {{ c.claim }}
              @if (c.sources?.length) { <span class="text-gray-400">— {{ c.sources!.join(', ') }}</span> }
              @else if (c.chunks.length) { <span class="text-gray-400">(chunks {{ c.chunks.join(', ') }})</span> }
            </div>
            }
            @if ((r.lookups ?? []).length) {
            <button class="text-indigo-600 underline"
                    (click)="openLookup.emit(r.lookups![0].index)">
              view retrieval proof{{ r.lookups![0].exact ? '' : ' (inferred pairing)' }}
            </button>
            }
          </div>
          }
        </div>
        }
      </div>
      }
    </div>
    }
  `,
})
export class CallAuditComponent {
  @Input({ required: true }) set report(r: SessionReport | null | undefined) {
    this.audit.set(r?.callAudit ?? null);
    this.expanded.set(new Set());
  }
  /** Parent-supplied re-run (the rag-log auditFn pattern: no service injection here). */
  @Input() rerunFn: (() => Promise<CallAudit>) | null = null;
  /** Jump to one lookup in the RAG tab — the parent switches tabs + focuses it. */
  @Output() openLookup = new EventEmitter<number>();

  readonly audit = signal<CallAudit | null>(null);
  readonly expanded = signal<Set<number>>(new Set());
  readonly running = signal(false);

  dims(): Array<{ key: string; value: number | null }> {
    const d = this.audit()?.dimensionAverages ?? {};
    return (['accuracy', 'relevance', 'completeness', 'grounding'] as const)
      .map((key) => ({ key, value: (d as Record<string, number | null>)[key] ?? null }));
  }

  scoreLine(s: CallAuditScores | null | undefined): string {
    if (!s) return '';
    const v = (x: number | null | undefined) => (x == null ? '—' : String(x));
    return `A${v(s.accuracy)} R${v(s.relevance)} C${v(s.completeness)} G${v(s.grounding)}`;
  }

  lookupIndexOf(evidence: Record<string, unknown> | null | undefined): number | null {
    const i = (evidence as { lookupIndex?: unknown } | null)?.lookupIndex;
    return typeof i === 'number' ? i : null;
  }

  jumpToLookup(evidence: Record<string, unknown> | null | undefined): void {
    const i = this.lookupIndexOf(evidence);
    if (i !== null) this.openLookup.emit(i);
  }

  toggle(i: number): void {
    this.expanded.update((s) => {
      const next = new Set(s);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  }

  async rerun(): Promise<void> {
    if (!this.rerunFn || this.running()) return;
    this.running.set(true);
    try {
      this.audit.set(await this.rerunFn());
    } catch {
      /* the button simply stays available; the report is unchanged */
    } finally {
      this.running.set(false);
    }
  }
}
